import { Button } from "@progress/kendo-react-buttons";
import { Notification } from "@progress/kendo-react-notification";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Provider as JotaiProvider } from "jotai";
import React from "react";
import { createRoot } from "react-dom/client";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { createBrowserRouter, RouterProvider } from "react-router";
import { KendoSnackbarProvider } from "@/components/Snackbar/KendoSnackbarProvider";
import { URLs } from "@/configs/menu";
import { TitleProvider } from "@/context/TitleContext";
import { UserProvider } from "@/context/UserContext";
import { initConfig, setEnvs } from "@/helpers/features";
import { initializeTheme } from "@/helpers/theme";
import loggerClient from "@/libs/LoggerClient";
import { routes } from "@/Routes/routes";
import "./styles/index.scss";

// Dev-only: keeps @tanstack/react-query-devtools out of the production bundle.
const ReactQueryDevtools = React.lazy(() =>
  import("@tanstack/react-query-devtools").then(m => ({
    default: m.ReactQueryDevtools,
  })),
);

initConfig();
setEnvs();
initializeTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
    },
  },
});

const ErrorFallback: React.FC<FallbackProps> = ({
  error,
  resetErrorBoundary,
}) => {
  loggerClient.recordError(error);
  return (
    <div
      role="alert"
      style={{
        padding: "1rem",
        margin: "1rem",
        border: "1px solid var(--kendo-color-error)",
        borderRadius: "4px",
        backgroundColor: "var(--tlx-surface-bg-surface)",
      }}
    >
      <Notification type={{ style: "error" }} style={{ marginBottom: "1rem" }}>
        <div>
          <p style={{ fontWeight: 600, marginBottom: "0.5rem" }}>
            Something went wrong:
          </p>
          <pre
            style={{
              margin: "0.5rem 0",
              fontSize: "var(--tlx-font-size-12)",
              overflow: "auto",
            }}
          >
            {error?.message}
          </pre>
          <pre
            style={{
              margin: "0.5rem 0",
              fontSize: "var(--tlx-font-size-12)",
              overflow: "auto",
            }}
          >
            {error?.stack}
          </pre>
        </div>
      </Notification>
      <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
        <Button
          size="medium"
          onClick={() => (window.location.href = URLs.accounts)}
          fillMode="outline"
        >
          Go To Home
        </Button>
        <Button size="medium" onClick={resetErrorBoundary} fillMode="outline">
          Retry
        </Button>
      </div>
    </div>
  );
};

const router = createBrowserRouter(routes);

const container = document.getElementById("root") as HTMLElement;
const root = createRoot(container);

document.getElementById("boot-loader")?.remove();

root.render(
  <JotaiProvider>
    <UserProvider>
      <TitleProvider>
        <ErrorBoundary
          fallbackRender={props => <ErrorFallback {...props} />}
          onReset={() => window.location.reload()}
        >
          <KendoSnackbarProvider>
            <QueryClientProvider client={queryClient}>
              <RouterProvider router={router} />
              {import.meta.env.DEV && (
                <React.Suspense fallback={null}>
                  <ReactQueryDevtools
                    initialIsOpen={false}
                    buttonPosition="bottom-left"
                  />
                </React.Suspense>
              )}
            </QueryClientProvider>
          </KendoSnackbarProvider>
        </ErrorBoundary>
      </TitleProvider>
    </UserProvider>
  </JotaiProvider>,
);
